import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  Calendar,
  User,
  MapPin,
  DollarSign,
  PlusCircle,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  ChevronRight,
  ShieldAlert,
  FileText
} from "lucide-react";

const getEmptyVisa = () => ({
  appliedDate: "",
  appliedThrough: "",
  agentName: "",
  howYouFoundAgent: "",
  feesPaid: "",
  biometricDate: "",
  biometricCity: "",
  issueDate: "",
  expiryDate: "",
});

const CanadaVisaForm = () => {
  const [visas, setVisas] = useState([getEmptyVisa()]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const fetchVisa = async () => {
      setLoading(true);
      try {
        const res = await makeRequestAuth("get", "/user/getCanadaVisa");
        if (res?.data?.visas?.length) {
          const normalizedVisas = res.data.visas.map(v => ({
            ...v,
            appliedDate: normalizeDate(v.appliedDate),
            biometricDate: normalizeDate(v.biometricDate),
            issueDate: normalizeDate(v.issueDate),
            expiryDate: normalizeDate(v.expiryDate),
          }));
          setVisas(normalizedVisas);
          setPreview({ ...res.data, visas: normalizedVisas });
        }
      } catch (err) {
        console.error("No previous data found");
      } finally {
        setLoading(false);
      }
    };
    fetchVisa();
  }, []);

  const normalizeDate = (date) => date ? date.split('T')[0] : "";



  const handleChange = (index, field, value) => {
    const updated = [...visas];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "appliedDate" && updated[index].issueDate < value) {
      updated[index].issueDate = "";
    }
    setVisas(updated);
  };

  const validateVisas = () => {
    let list = [];
    visas.forEach((v, i) => {
      const row = `Record ${i + 1}`;
      if (!v.appliedDate) list.push(`${row}: Applied Date is required`);
      if (!v.appliedThrough?.trim()) list.push(`${row}: 'Applied Through' is required`);
      if (!v.feesPaid || isNaN(v.feesPaid)) list.push(`${row}: Valid Fees in CAD is required`);
      if (v.issueDate && v.expiryDate && new Date(v.issueDate) >= new Date(v.expiryDate)) {
        list.push(`${row}: Expiry Date must be after the Issue Date`);
      }
    });
    return list;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateVisas();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setErrors([]);
    setLoading(true);
    try {
      const payload = {
        visas: visas.map(({ _id, ...rest }) => ({
          ...rest,
          feesPaid: Number(rest.feesPaid)
        }))
      }; const res = await makeRequestAuth("post", "/user/createOrUpdateCanadaVisa", payload);
      setPreview(res.data);
      showSuccessToast("Visa history updated successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Internal Server Error");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending" },
    approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <CheckCircle size={14} />, label: "Approve" },
    reject: { color: "bg-rose-100 text-rose-700 border-rose-200", icon: <XCircle size={14} />, label: "Rejected" },
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 font-normal">
      <ToastContainer />

      {/* LOADER */}
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-md z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-[#132b45] rounded-full animate-spin"></div>
            <p className="font-normal text-[#132b45] text-[10px] uppercase tracking-widest">Processing Application</p>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="bg-[#132b45] rounded-t-[2.5rem] p-8 text-white flex flex-col md:flex-row justify-between items-center gap-6 shadow-2xl relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-4">
          <div className="p-3 bg-red-500/20 rounded-2xl border border-red-500/30">
            <FileText size={30} className="text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-medium">Canada Visa History</h1>
            <p className="text-blue-100/60 text-xs mt-1 font-normal">IRCC Application Records Tracking</p>
          </div>
        </div>
        {preview && (
          <div className={`relative z-10 flex items-center gap-2 px-5 py-2 rounded-full border text-[10px] font-normal uppercase tracking-widest shadow-inner ${STATUS_CONFIG[preview.status?.toLowerCase()]?.color || "bg-slate-100 text-slate-600"}`}>
            {STATUS_CONFIG[preview.status?.toLowerCase()]?.icon}
            {STATUS_CONFIG[preview.status?.toLowerCase()]?.label || "Submitted"}
          </div>
        )}
      </div>

      <div className="bg-white rounded-b-[2.5rem] shadow-xl border-x border-b border-gray-100 p-8">
        {errors.length > 0 && (
          <div className="mb-8 bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-2xl animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-2 text-rose-800 font-medium mb-2 uppercase text-[10px] tracking-widest">
              <ShieldAlert size={16} /> Attention Required
            </div>
            <ul className="text-xs text-rose-600 space-y-1 ml-4 list-disc font-normal">
              {errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </div>
        )}

        {preview && !loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-500">
            {visas.map((v, i) => (
              <div key={i} className="bg-slate-50/50 border border-slate-100 rounded-[2rem] p-6 hover:bg-white transition-all hover:shadow-lg">
                <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-3">
                  <span className="text-[10px] font-normal text-blue-600 uppercase tracking-widest">Record {i + 1}</span>
                  <CheckCircle size={16} className="text-emerald-500" />
                </div>
                <div className="grid grid-cols-2 gap-y-5">
                  <PreviewItem label="Applied Date" value={formatDate(v.appliedDate)} />
                  <PreviewItem label="Channel" value={v.appliedThrough} />
                  <PreviewItem label="Agent" value={v.agentName} />
                  <PreviewItem label="Fees (CAD)" value={v.feesPaid} />
                  <PreviewItem label="Biometric City" value={v.biometricCity} />
                  <PreviewItem label="Issue Date" value={formatDate(v.issueDate)} />
                  <PreviewItem label="Expiry Date" value={formatDate(v.expiryDate)} />
                </div>
              </div>
            ))}
            <div className="md:col-span-2 flex justify-center pt-8">
              {(preview.status === "reject" || !preview.status) && (
                <button onClick={() => setPreview(null)} className="flex items-center gap-2 bg-[#132b45] text-white px-10 py-4 rounded-2xl font-normal hover:bg-black transition-all active:scale-95 shadow-xl shadow-blue-900/20">
                  <PlusCircle size={20} /> Update History
                </button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-10">
            {visas.map((visa, index) => (
              <div key={index} className="bg-white border-2 border-slate-50 rounded-[2.5rem] p-8 shadow-sm hover:border-blue-100 transition-all relative group">
                <div className="flex justify-between items-center mb-10">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#132b45] text-white w-10 h-10 rounded-2xl flex items-center justify-center font-normal text-sm">
                      {index + 1}
                    </span>
                    <h3 className="font-normal text-[#132b45] uppercase tracking-widest text-[10px]">Application Details</h3>
                  </div>
                  {visas.length > 1 && (
                    <button type="button" onClick={() => {
                      const filtered = visas.filter((_, i) => i !== index);
                      setVisas(filtered);
                    }} className="text-rose-400 hover:text-rose-600 p-2 hover:bg-rose-50 rounded-xl transition-all">
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <InputField
                    type="date"
                    label="Applied Date *"
                    icon={<Calendar size={14} />}
                    max={new Date().toISOString().split('T')[0]}
                    value={visa.appliedDate}
                    onChange={(v) => handleChange(index, "appliedDate", v)}
                  />
                  <InputField
                    label="Applied Through *"
                    icon={<ChevronRight size={14} />}
                    placeholder="e.g. Online, Agent, Paper"
                    value={visa.appliedThrough}
                    onChange={(v) => handleChange(index, "appliedThrough", v)}
                  />
                  <InputField
                    label="Agent Name"
                    icon={<User size={14} />}
                    placeholder="Full name"
                    value={visa.agentName}
                    onChange={(v) => handleChange(index, "agentName", v)}
                  />
                  <InputField
                    type="number"
                    label="Fees Paid (CAD) *"
                    icon={<DollarSign size={14} />}
                    placeholder="0.00"
                    value={visa.feesPaid}
                    onChange={(v) => handleChange(index, "feesPaid", v)}
                  />
                  <InputField
                    type="date"
                    label="Biometric Date"
                    icon={<Calendar size={14} />}
                    min={visa.appliedDate}
                    value={visa.biometricDate}
                    onChange={(v) => handleChange(index, "biometricDate", v)}
                  />
                  <InputField
                    label="Biometric City"
                    icon={<MapPin size={14} />}
                    placeholder="e.g. Chandigarh"
                    value={visa.biometricCity}
                    onChange={(v) => handleChange(index, "biometricCity", v)}
                  />
                  <InputField
                    type="date"
                    label="Issue Date"
                    icon={<CheckCircle size={14} />}
                    min={visa.appliedDate}
                    value={visa.issueDate}
                    onChange={(v) => handleChange(index, "issueDate", v)}
                  />
                  <InputField
                    type="date"
                    label="Expiry Date"
                    icon={<XCircle size={14} />}
                    min={visa.issueDate || visa.appliedDate}
                    value={visa.expiryDate}
                    onChange={(v) => handleChange(index, "expiryDate", v)}
                  />
                  <InputField
                    label="How Found Agent"
                    icon={<Search size={14} />}
                    placeholder="Referral / Social Media"
                    value={visa.howYouFoundAgent}
                    onChange={(v) => handleChange(index, "howYouFoundAgent", v)}
                  />
                </div>
              </div>
            ))}

            <div className="flex flex-col md:flex-row gap-4 justify-between items-center pt-6 border-t border-slate-50">
              <button type="button" onClick={() => setVisas([...visas, getEmptyVisa()])} className="flex items-center gap-2 text-[#132b45] font-normal uppercase tracking-widest text-[10px] bg-slate-50 hover:bg-slate-100 px-8 py-5 rounded-[2rem] transition-all border-2 border-transparent hover:border-slate-200">
                <PlusCircle size={18} /> Add New Entry
              </button>

              <button type="submit" disabled={loading} className="w-full md:w-auto bg-[#132b45] hover:bg-black text-white px-16 py-5 rounded-[2rem] font-normal text-base shadow-2xl transition-all hover:-translate-y-1 active:scale-95 flex items-center justify-center gap-3">
                {loading ? "Processing..." : "Save Details"} <ChevronRight size={20} />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const InputField = ({ label, value, onChange, type = "text", placeholder, icon, max, min }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 ml-1 text-slate-400">
      {icon}
      <label className="text-[10px] font-normal uppercase tracking-widest">{label}</label>
    </div>
    <input
      type={type}
      value={value || ""}
      max={max}
      min={min}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-3.5 focus:bg-white focus:border-[#132b45] outline-none transition-all font-normal text-slate-700 text-sm"
    />
  </div>
);

const PreviewItem = ({ label, value }) => (
  <div className="space-y-0.5">
    <p className="text-[10px] uppercase font-normal text-slate-400 tracking-tighter">{label}</p>
    <p className="text-sm font-normal text-[#132b45] leading-tight">{value || "—"}</p>
  </div>
);

export default CanadaVisaForm;